import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fmtErr } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2 } from "lucide-react";

const HEROES = [
  "https://images.unsplash.com/photo-1711962135760-ec08ef3ddbef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxzdXNwZW5kZWQlMjBzd2ltbWluZyUyMHBvb2wlMjBjb25zdHJ1Y3Rpb24lMjBhcmNoaXRlY3R1cmUlMjBsdXh1cnl8ZW58MHx8fHwxNzg4NDMyMDIwfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBjb25zdHJ1Y3Rpb24lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg4NTQ2Nzg1fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1613977257365-aaae5a9817ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBjb25zdHJ1Y3Rpb24lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg4NTQ2Nzg1fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1602774895312-f002b8f2f33b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBjb25zdHJ1Y3Rpb24lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg4NTQ2Nzg1fDA&ixlib=rb-4.1.0&q=85",
];

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [hero] = useState(() => HEROES[Math.floor(Math.random() * HEROES.length)]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(fmtErr(err, t("login.failed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="relative hidden w-1/2 overflow-hidden bg-slate-900 lg:block">
        <img src={hero} alt="Suspended pool construction" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-2 text-white">
            <img src="/pruva-logo-icon.png" alt="Pruva" className="h-8 w-auto" />
            <div>
              <div className="font-display text-xl font-extrabold tracking-tight">PRUVA</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Project Tracking</div>
            </div>
          </div>
          <div className="text-white">
            <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight">
              Pruva Acrylic Swimming Pools
              <br />
              {t("login.controlCenter")}
            </h1>
            <p className="mt-4 max-w-md text-sm italic leading-relaxed text-slate-300" data-testid="login-footnote">
              Çocukluk hayalim değildi, profesyonel olarak yapıyorum.
            </p>
          </div>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center p-6">
        <div className="absolute top-4 ltr:right-4 rtl:left-4">
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <img src="/pruva-logo-full.png" alt="Pruva Swimming Pools" className="h-11 w-auto" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Project Tracking</span>
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{t("login.signIn")}</h2>
          <p className="mt-1 text-sm text-slate-500">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="login-error">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" placeholder="you@company.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("login.password")}</Label>
              <Input id="password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" placeholder="••••••••" />
            </div>
            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("login.signIn")}
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-sky-700 hover:underline" data-testid="forgot-password-link">
                {t("login.forgot")}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
